import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Trash2,
  Edit2,
  Plus,
  X,
  Upload,
  User,
  Search,
  Image as ImageIcon,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { createArtistWithUrls, updateArtistWithUrls, deleteArtist } from '../services/api';
import { uploadArtistProfileToBunny, uploadArtistCoverToBunny } from '../services/bunnyUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  return `${BACKEND_URL}${imagePath}`;
}

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function useDropzone(onFile) {
  const [isOver, setIsOver] = useState(false);

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  return { isOver, onDragOver, onDragLeave, onDrop };
}

export default function ArtistManager({ artists, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const [profilePreview, setProfilePreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const [query, setQuery] = useState('');

  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const filteredArtists = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return artists;
    return artists.filter((a) => {
      const name = (a.name || '').toLowerCase();
      const bio = (a.bio || '').toLowerCase();
      return name.includes(q) || bio.includes(q);
    });
  }, [artists, query]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (!showModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal]);

  const resetModalState = () => {
    setShowModal(false);
    setEditingArtist(null);
    setProfilePreview(null);
    setCoverPreview(null);
    setProfileFile(null);
    setCoverFile(null);
    setUploadProgress(0);
    setUploadStatus('');
  };

  const openModal = (artist = null) => {
    setEditingArtist(artist);
    setShowModal(true);

    setProfileFile(null);
    setCoverFile(null);

    if (artist) {
      setProfilePreview(getImageUrl(artist.profile_image));
      setCoverPreview(getImageUrl(artist.cover_image));
    } else {
      setProfilePreview(null);
      setCoverPreview(null);
    }

    setTimeout(() => {
      const el = document.querySelector('input[name="name"]');
      if (el) el.focus();
    }, 60);
  };

  const setProfileFromFile = (file) => {
    if (!file) return;
    setProfileFile(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const setCoverFromFile = (file) => {
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const profileDrop = useDropzone(setProfileFromFile);
  const coverDrop = useDropzone(setCoverFromFile);

  const handleProfileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setProfileFromFile(file);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setCoverFromFile(file);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet artiste ?')) return;
    try {
      await deleteArtist(id);
      onRefresh();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData(e.target);
      const name = String(formData.get('name') || '').trim();
      const bio = String(formData.get('bio') || '').trim();

      if (!name) {
        alert('Le nom est obligatoire.');
        setLoading(false);
        return;
      }

      let profileImageUrl = editingArtist?.profile_image || null;
      let coverImageUrl = editingArtist?.cover_image || null;

      if (profileFile) {
        setUploadStatus('📤 Upload photo de profil...');
        profileImageUrl = await uploadArtistProfileToBunny(profileFile, (progress) => {
          setUploadProgress(Math.round(progress * 0.45));
        });
      }

      if (coverFile) {
        setUploadStatus('📤 Upload image de couverture...');
        coverImageUrl = await uploadArtistCoverToBunny(coverFile, (progress) => {
          setUploadProgress(45 + Math.round(progress * 0.35));
        });
      }

      setUploadStatus('💾 Enregistrement...');
      setUploadProgress(85);

      const artistData = {
        name,
        bio: bio || '',
        profile_image: profileImageUrl,
        cover_image: coverImageUrl,
      };

      if (editingArtist) {
        await updateArtistWithUrls(editingArtist.id, artistData);
      } else {
        await createArtistWithUrls(artistData);
      }

      setUploadProgress(100);
      setUploadStatus('✅ Artiste enregistré !');

      setTimeout(() => {
        resetModalState();
        e.target.reset();
        onRefresh();
      }, 450);
    } catch (error) {
      console.error('Error:', error);
      alert('Erreur: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showModal) return;
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape' && !loading) resetModalState();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showModal, loading]);

  const total = artists.length;
  const shown = filteredArtists.length;

  return (
    <div className="relative">
      {/* Background vibe */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 bg-fuchsia-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -top-20 -right-24 w-80 h-80 bg-cyan-500/15 blur-3xl" />

      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-2 px-2 pt-2 pb-4 backdrop-blur-xl bg-black/20">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 md:p-5 shadow-[0_20px_80px_-40px_rgba(0,0,0,0.8)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-3 py-1.5">
                  <Sparkles size={16} className="text-white/70" />
                  <span className="text-xs text-white/70">Artists</span>
                </div>
                <div className="text-xs text-white/45">
                  {shown}/{total} affiché{shown > 1 ? 's' : ''}
                </div>
              </div>

 
  
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative w-full sm:w-96">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un artiste…"
                  className="w-full pl-10 pr-10 py-3 rounded-2xl bg-white/5 border border-white/10
                             focus:outline-none focus:border-white/25 focus:bg-white/10 transition"
                />
                {query?.trim() ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl hover:bg-white/10 transition"
                    aria-label="Effacer"
                    title="Effacer"
                  >
                    <X size={16} className="text-white/60" />
                  </button>
                ) : null}
              </div>

              <button
                onClick={() => openModal()}
                className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl
                           bg-white text-black font-semibold hover:bg-white/90 transition active:scale-[0.99]"
              >
                <Plus size={18} />
                Nouveau
                <ChevronRight size={18} className="opacity-60 group-hover:opacity-90 transition" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {artists.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <User className="text-white/60" />
          </div>
          <p className="text-white/85 font-semibold text-lg">Aucun artiste</p>
          <p className="text-white/55 mt-1">
            Ajoute ton premier artiste pour commencer.
          </p>
          <button
            onClick={() => openModal()}
            className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl
                       bg-white text-black font-semibold hover:bg-white/90 transition"
          >
            <Plus size={18} />
            Créer un artiste
          </button>
        </div>
      ) : filteredArtists.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <Search className="text-white/60" />
          </div>
          <p className="text-white/85 font-semibold text-lg">Aucun résultat</p>
          <p className="text-white/55 mt-1">Essaie un autre mot-clé.</p>
          <button
            onClick={() => setQuery('')}
            className="mt-6 px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition"
          >
            Effacer la recherche
          </button>
        </div>
      ) : (
        <>
          {/* Business cards grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredArtists.map((artist) => {
              const coverUrl = getImageUrl(artist.cover_image);
              const profileUrl = getImageUrl(artist.profile_image);

              return (
                <div
                  key={artist.id}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04]
                             hover:bg-white/[0.07] hover:border-white/15 transition
                             shadow-[0_18px_60px_-45px_rgba(0,0,0,0.9)]"
                >
                  {/* Cover (top) */}
                  <div className="relative h-20">
                    <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/35 via-indigo-500/25 to-cyan-500/25" />
                    {coverUrl ? (
                      <img
                        loading="lazy"
                        decoding="async"
                        src={coverUrl}
                        alt="Cover"
                        className="absolute inset-0 w-full h-full object-cover opacity-85"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/60">
                        <ImageIcon size={18} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Actions (top-right) */}
                    <div className="absolute top-2 right-2 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => openModal(artist)}
                        className="p-2 rounded-2xl bg-black/40 border border-white/15 hover:bg-black/55 transition"
                        title="Modifier"
                        aria-label="Modifier"
                      >
                        <Edit2 size={14} className="text-white/85" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(artist.id)}
                        className="p-2 rounded-2xl bg-black/40 border border-white/15 hover:bg-black/55 transition"
                        title="Supprimer"
                        aria-label="Supprimer"
                      >
                        <Trash2 size={14} className="text-red-200" />
                      </button>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="px-4 pb-4 pt-0">
                    {/* Avatar overlap */}
                    <div className="-mt-7 flex items-end justify-between gap-3">
                      <div className="relative shrink-0">
                        {profileUrl ? (
                          <img
                            loading="lazy"
                            decoding="async"
                            src={profileUrl}
                            alt={artist.name}
                            className="w-14 h-14 rounded-2xl object-cover border border-white/20 shadow-xl"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shadow-xl">
                            <User size={20} className="text-white/60" />
                          </div>
                        )}
                        <div className="pointer-events-none absolute -inset-6 opacity-0 group-hover:opacity-100 transition">
                          <div className="w-full h-full bg-cyan-400/10 blur-2xl rounded-full" />
                        </div>
                      </div>

                      {/* Small badges */}
                      <div className="flex flex-wrap justify-end gap-2 pb-1">
                        <span className="text-[11px] text-white/70 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                          {artist.profile_image ? 'Profil ✅' : 'Profil —'}
                        </span>
                        <span className="text-[11px] text-white/70 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                          {artist.cover_image ? 'Cover ✅' : 'Cover —'}
                        </span>
                      </div>
                    </div>

                    {/* Name + bio */}
                    <div className="mt-3">
                      <h3 className="text-base font-semibold truncate">{artist.name}</h3>
                      <p className="mt-1 text-sm text-white/55 leading-snug line-clamp-2">
                        {artist.bio ? artist.bio : 'Aucune biographie.'}
                      </p>
                    </div>

                    {/* Bottom row */}
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-[11px] text-white/45 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                        ID: {artist.id}
                      </span>

                      {/* Mobile actions */}
                      <div className="flex gap-2 sm:hidden">
                        <button
                          type="button"
                          onClick={() => openModal(artist)}
                          className="px-3 py-2 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition text-sm"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(artist.id)}
                          className="px-3 py-2 rounded-2xl bg-red-500/10 hover:bg-red-500/15 border border-red-400/20 transition text-sm text-red-200"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* subtle shine */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
                    <div className="absolute -top-16 right-6 w-40 h-40 bg-fuchsia-500/10 blur-3xl" />
                    <div className="absolute -bottom-16 left-6 w-40 h-40 bg-cyan-500/10 blur-3xl" />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-sm text-white/45">
            {shown} artiste{shown > 1 ? 's' : ''} affiché{shown > 1 ? 's' : ''} (sur {total})
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 modal-text"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !loading) resetModalState();
          }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          <div className="relative mx-auto mt-6 mb-6 w-[min(980px,calc(100%-24px))]">
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#07080b] shadow-[0_40px_120px_-60px_rgba(0,0,0,0.9)]">
              {/* Top glows */}
              <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 bg-fuchsia-500/20 blur-3xl" />
              <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-cyan-500/15 blur-3xl" />

              {/* Upload overlay */}
              {loading && uploadProgress > 0 && (
                <div className="absolute inset-0 bg-black/85 backdrop-blur-sm z-20 flex items-center justify-center p-8">
                  <div className="w-full max-w-md">
                    <div className="flex items-center justify-between text-white/80 text-sm mb-3">
                      <span>{uploadStatus}</span>
                      <span className="tabular-nums">{clamp(uploadProgress, 0, 100)}%</span>
                    </div>
                    <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-white rounded-full transition-all duration-300"
                        style={{ width: `${clamp(uploadProgress, 0, 100)}%` }}
                      />
                    </div>
                    <div className="mt-4 text-xs text-white/45">
                      Ne ferme pas la fenêtre pendant l’upload.
                    </div>
                  </div>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-white/10">
                <div className="min-w-0">
                  <h3 className="text-xl md:text-2xl font-semibold tracking-tight">
                    {editingArtist ? "Modifier l'artiste" : 'Créer un artiste'}
                  </h3>
                  <p className="text-white/55 text-sm mt-1">
                    Drag & drop tes images — preview instantanée.
                  </p>
                </div>

                <button
                  onClick={resetModalState}
                  disabled={loading}
                  className="p-2.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition disabled:opacity-50"
                  aria-label="Fermer"
                >
                  <X size={18} className="text-white/80" />
                </button>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-[1fr_420px] gap-0">
                {/* LEFT: Preview panel */}
                <div className="relative border-b md:border-b-0 md:border-r border-white/10 p-6 md:p-8">
                  {/* Cover preview */}
                  <div
                    className={cn(
                      'relative h-44 md:h-52 rounded-3xl overflow-hidden border border-white/10 bg-white/5',
                      coverDrop.isOver ? 'ring-2 ring-white/40' : 'ring-0'
                    )}
                    onDragOver={coverDrop.onDragOver}
                    onDragLeave={coverDrop.onDragLeave}
                    onDrop={coverDrop.onDrop}
                    onClick={() => coverInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/25 via-indigo-500/15 to-cyan-500/15" />
                    {coverPreview ? (
                      <img
                        loading="lazy"
                        decoding="async"
                        src={coverPreview}
                        alt="Cover preview"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                          <Upload size={20} className="text-white/70" />
                        </div>
                        <div className="mt-3 text-sm font-semibold text-white/80">
                          Dépose une couverture ici
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          ou clique pour choisir un fichier
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                    <div className="absolute bottom-3 left-3 text-xs text-white/80 bg-black/40 border border-white/15 px-3 py-1.5 rounded-full">
                      Couverture
                    </div>
                  </div>

                  <input
                    ref={coverInputRef}
                    type="file"
                    name="cover"
                    accept="image/*"
                    onChange={handleCoverChange}
                    className="hidden"
                    disabled={loading}
                  />

                  {/* Avatar preview */}
                  <div className="mt-5 flex items-center gap-4">
                    <div
                      className={cn(
                        'relative w-20 h-20 rounded-3xl overflow-hidden border border-white/10 bg-white/5 cursor-pointer',
                        profileDrop.isOver ? 'ring-2 ring-white/40' : 'ring-0'
                      )}
                      onDragOver={profileDrop.onDragOver}
                      onDragLeave={profileDrop.onDragLeave}
                      onDrop={profileDrop.onDrop}
                      onClick={() => profileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                    >
                      {profilePreview ? (
                        <img
                          loading="lazy"
                          decoding="async"
                          src={profilePreview}
                          alt="Profile preview"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <User size={24} className="text-white/60" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    </div>

                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white/80">Photo de profil</div>
                      <div className="text-xs text-white/50">
                        Drag & drop • ou clique • carré recommandé
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => profileInputRef.current?.click()}
                          disabled={loading}
                          className="px-3 py-2 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition text-sm disabled:opacity-50"
                        >
                          Remplacer
                        </button>

                        {(profilePreview || profileFile) && (
                          <button
                            type="button"
                            onClick={() => {
                              setProfileFile(null);
                              setProfilePreview(editingArtist ? getImageUrl(editingArtist.profile_image) : null);
                            }}
                            disabled={loading}
                            className="px-3 py-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition text-sm text-white/70 disabled:opacity-50"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <input
                    ref={profileInputRef}
                    type="file"
                    name="profile_image"
                    accept="image/*"
                    onChange={handleProfileChange}
                    className="hidden"
                    disabled={loading}
                  />

                  {/* Little preview card */}
                  <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-xs text-white/50">Aperçu</div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/10 overflow-hidden flex items-center justify-center">
                        {profilePreview ? (
                          <img src={profilePreview} alt="mini" className="w-full h-full object-cover" />
                        ) : (
                          <User size={18} className="text-white/60" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white/85 truncate">
                          {editingArtist?.name || 'Nom de l’artiste'}
                        </div>
                        <div className="text-xs text-white/50 truncate">
                          {editingArtist?.bio || 'Biographie…'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Form */}
                <div className="p-6 md:p-8">
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Nom <span className="text-white/40">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        defaultValue={editingArtist?.name}
                        required
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                                   focus:outline-none focus:border-white/25 focus:bg-white/10 transition disabled:opacity-50"
                        placeholder="Nom de l’artiste"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Biographie
                      </label>
                      <textarea
                        name="bio"
                        defaultValue={editingArtist?.bio}
                        rows={6}
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                                   focus:outline-none focus:border-white/25 focus:bg-white/10 transition disabled:opacity-50 resize-none"
                        placeholder="Décris l’artiste (style, univers, liens…)…"
                      />
                      <div className="mt-2 text-xs text-white/45">
                        Conseil : 2–3 phrases = meilleur rendu.
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <button
                        type="button"
                        onClick={resetModalState}
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 transition disabled:opacity-50"
                      >
                        Annuler
                      </button>

                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-2xl bg-white text-black font-semibold hover:bg-white/90 transition disabled:opacity-50"
                      >
                        {loading ? 'Enregistrement…' : editingArtist ? 'Enregistrer' : 'Créer'}
                      </button>
                    </div>

                    <div className="text-xs text-white/45">
                      Raccourci : <span className="text-white/70">ESC</span> pour fermer (hors upload).
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
